import type { MigrationSecurity } from '@/features/migrations/types';

export interface ProviderPreset {
  key: string;
  label: string;
  host: string;
  port: number;
  security: MigrationSecurity;
  /** Shown under the form: what the customer must do first. */
  note?: string;
  /** Provider that no longer accepts IMAP passwords (OAuth only). */
  unsupported?: boolean;
}

/** Common source providers. Hosts are the providers' documented public IMAP endpoints. */
export const PROVIDER_PRESETS: ProviderPreset[] = [
  { key: 'other', label: 'Other IMAP server (cPanel, Plesk, previous host)', host: '', port: 993, security: 'ssl',
    note: 'Use the IMAP server name from the previous host (often mail.<domain>). Port 993 uses SSL/TLS; port 143 uses STARTTLS.' },
  { key: 'gmail', label: 'Gmail / Google Workspace', host: 'imap.gmail.com', port: 993, security: 'ssl',
    note: 'Turn on 2-Step Verification for the Google account and create an app password; use it instead of the normal password. IMAP must be enabled in Gmail settings.' },
  { key: 'yahoo', label: 'Yahoo Mail', host: 'imap.mail.yahoo.com', port: 993, security: 'ssl',
    note: 'Generate an app password under Yahoo Account Security and use it here.' },
  { key: 'icloud', label: 'iCloud Mail', host: 'imap.mail.me.com', port: 993, security: 'ssl',
    note: 'Create an app-specific password at appleid.apple.com. The username is the full iCloud email address.' },
  { key: 'm365', label: 'Microsoft 365 / Outlook.com', host: 'outlook.office365.com', port: 993, security: 'ssl', unsupported: true,
    note: 'Microsoft has turned off password (basic) authentication for IMAP, so these accounts cannot be migrated with a password yet. An OAuth connector is planned.' },
];

export const presetByKey = (key: string): ProviderPreset => PROVIDER_PRESETS.find((p) => p.key === key) ?? PROVIDER_PRESETS[0];

/** The conventional port for a security mode, used when the user switches modes. */
export const defaultPort = (s: MigrationSecurity): number => (s === 'ssl' ? 993 : 143);
